// ============================================================
// src/pages/MyTimesheetPage.jsx
// All dates use YYYY-MM-DD format
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getMyWeek, addEntry, updateEntry, deleteEntry, submitWeek } from '../api/timesheetApi';
import {
  getDayLabel, formatTime, toDisplay, getToday, getCurrentWeekEnding,
  formatFromDate, parseDate,
} from '../utils/dateHelper';
import { Toast, StatCard, Btn, Field, Input, Modal, Select, Option, StatusBadge, Spinner, CardTitle, PageHeader } from '../components/UI';
import { useToast } from '../hooks/useToast';

// ════════════════════════════════════════════════════════════
// ── Custom DatePicker (zero deps, position:fixed popup) ─────
// ════════════════════════════════════════════════════════════
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function to12Hour(timeStr) {
  if (!timeStr) return '—';
  const [h, m] = timeStr.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return '—';
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

function DatePicker({ value, onChange, small = false }) {
  const selected = parseDate(value);
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState((selected || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((selected || today).getMonth());
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  useEffect(() => {
    const d = parseDate(value);
    if (d) { setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); }
  }, [value]);

  const prevMonth = () => viewMonth === 0 ? (setViewMonth(11), setViewYear(y => y - 1)) : setViewMonth(m => m - 1);
  const nextMonth = () => viewMonth === 11 ? (setViewMonth(0), setViewYear(y => y + 1)) : setViewMonth(m => m + 1);

  function buildDays() {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    return cells;
  }

  const isSelected = d => d && selected &&
    selected.getFullYear() === viewYear && selected.getMonth() === viewMonth && selected.getDate() === d;
  const isToday = d => d &&
    today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === d;

  function selectDay(day) {
    if (!day) return;
    onChange(formatFromDate(new Date(viewYear, viewMonth, day)));
    setOpen(false);
  }

  const years = [];
  for (let y = today.getFullYear() - 5; y <= today.getFullYear() + 5; y++) years.push(y);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      {/* Trigger */}
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          type="text" readOnly
          value={value || ''}
          placeholder="yyyy-mm-dd"
          onClick={() => setOpen(o => !o)}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: `1.5px solid ${open ? '#dc2626' : '#d1d5db'}`,
            borderRadius: '6px',
            padding: small ? '0.28rem 2.2rem 0.28rem 0.55rem' : '0.48rem 2.4rem 0.48rem 0.75rem',
            fontSize: small ? '0.8rem' : '0.875rem',
            color: value ? '#111827' : '#9ca3af',
            background: '#fff', outline: 'none', cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
        />
        <span onClick={() => setOpen(o => !o)} style={{
          position: 'absolute', right: '0.55rem', top: '50%',
          transform: 'translateY(-50%)',
          fontSize: small ? '0.9rem' : '1rem',
          cursor: 'pointer', userSelect: 'none', lineHeight: 1,
        }}>📅</span>
      </div>

      {/* Popup — fixed, auto-flips */}
      {open && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0,
            zIndex: 99999, background: '#fff',
            border: '1px solid #e5e7eb', borderRadius: '12px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            width: '288px', userSelect: 'none',
          }}
          ref={el => {
            if (!el || !wrapRef.current) return;
            const rect = wrapRef.current.getBoundingClientRect();
            const popH = el.offsetHeight || 300;
            const below = window.innerHeight - rect.bottom;
            el.style.top = below >= popH + 8 || below >= rect.top
              ? `${rect.bottom + 6}px`
              : `${rect.top - popH - 6}px`;
            let left = rect.left;
            if (left + 288 > window.innerWidth - 8) left = window.innerWidth - 296;
            if (left < 8) left = 8;
            el.style.left = `${left}px`;
          }}
        >
          {/* Header */}
          <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <button onClick={prevMonth} style={navBtn}>‹</button>
              <div style={{ display: 'flex', gap: '6px' }}>
                <select value={viewMonth} onChange={e => setViewMonth(+e.target.value)} style={dropStyle}>
                  {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </select>
                <select value={viewYear} onChange={e => setViewYear(+e.target.value)} style={dropStyle}>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <button onClick={nextMonth} style={navBtn}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
              {DAYS.map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: '0.62rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', lineHeight: '26px' }}>{d}</div>
              ))}
            </div>
          </div>

          {/* Days grid */}
          <div style={{ padding: '8px 10px 12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
              {buildDays().map((day, i) => (
                <div key={i} onClick={() => selectDay(day)} style={{
                  height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '7px', fontSize: '0.82rem',
                  cursor: day ? 'pointer' : 'default',
                  fontWeight: isSelected(day) || isToday(day) ? 700 : 400,
                  background: isSelected(day) ? '#dc2626' : 'transparent',
                  color: isSelected(day) ? '#fff' : isToday(day) ? '#dc2626' : day ? '#374151' : 'transparent',
                  transition: 'background 0.1s, color 0.1s',
                }}
                  onMouseEnter={e => { if (day && !isSelected(day)) { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; } }}
                  onMouseLeave={e => { if (day && !isSelected(day)) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isToday(day) ? '#dc2626' : '#374151'; } }}
                >{day || ''}</div>
              ))}
            </div>
            {/* Today shortcut */}
            <div style={{ textAlign: 'center', marginTop: '8px', borderTop: '1px solid #f3f4f6', paddingTop: '8px' }}>
              <button onClick={() => { onChange(getToday()); setOpen(false); }} style={{
                background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px',
                padding: '3px 14px', fontSize: '0.75rem', fontWeight: 600,
                color: '#374151', cursor: 'pointer',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#dc2626'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
              >Today</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn = { background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', color: '#6b7280', lineHeight: 1 };
const dropStyle = { fontSize: '0.78rem', fontWeight: 600, border: '1px solid #e5e7eb', borderRadius: '5px', padding: '3px 6px', color: '#374151', background: '#f9fafb', outline: 'none', cursor: 'pointer' };

function getTruncLen(width = window.innerWidth) {
  if (width < 640) return 50;
  if (width < 1024) return 55;
  return 60;
}

function useTextTruncLen() {
  const [truncLen, setTruncLen] = useState(() => getTruncLen());
  useEffect(() => {
    const onResize = () => setTruncLen(getTruncLen());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return truncLen;
}

function TruncatedText({ text, maxLen, style }) {
  if (!text) return null;
  const trimmed = text.trim();
  const isLong = trimmed.length > maxLen;
  const display = isLong ? `${trimmed.slice(0, maxLen).trimEnd()}…` : trimmed;
  return (
    <div style={{ ...style, cursor: isLong ? 'pointer' : undefined }} title={isLong ? trimmed : undefined}>
      {display}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ── Page ────────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════
export default function MyTimesheetPage() {
  const { toast, showToast, clearToast } = useToast();
  const truncLen = useTextTruncLen();

  const [weekEnding, setWeekEnding] = useState(getCurrentWeekEnding());
  const [week, setWeek] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [viewEntry, setViewEntry] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const updEdit = k => e => setEditForm(f => ({ ...f, [k]: e.target.value }));

  const initForm = () => ({
    entry_date: getToday(),
    work_order: '',
    job_number: '',
    location: 'Shop',
    description: '',
    notes: '',
    time_in: '07:00',
    time_out: '15:30',
    hours_worked: '',
  });
  const [form, setForm] = useState(initForm);
  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Convert minutes to HH.MM style (e.g. 1h 55m => "1.55")
  const toHourMinuteString = totalMinutes => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}.${String(minutes).padStart(2, '0')}`;
  };

  // Parse stored hour value (HH.MM) into minutes.
  const toMinutes = value => {
    if (value === null || value === undefined || value === '') return 0;
    const [hPart, mPart = '00'] = String(value).split('.');
    const h = Number(hPart);
    const m = Number((mPart || '00').padEnd(2, '0').slice(0, 2));
    if (Number.isNaN(h) || Number.isNaN(m)) return 0;
    return (h * 60) + Math.min(Math.max(m, 0), 59);
  };

  // ── Auto-calc hours — Add form ──
  useEffect(() => {
    const [h1, m1] = form.time_in.split(':').map(Number);
    const [h2, m2] = form.time_out.split(':').map(Number);
    if (isNaN(h1) || isNaN(h2)) return;
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (mins < 0) mins += 1440;
    setForm(f => ({ ...f, hours_worked: mins > 0 ? toHourMinuteString(mins) : '' }));
  }, [form.time_in, form.time_out]);

  // ── Auto-calc hours — Edit form ──
  useEffect(() => {
    if (!editForm) return;
    const [h1, m1] = editForm.time_in.split(':').map(Number);
    const [h2, m2] = editForm.time_out.split(':').map(Number);
    if (isNaN(h1) || isNaN(h2)) return;
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (mins < 0) mins += 1440;
    setEditForm(f => ({ ...f, hours_worked: mins > 0 ? toHourMinuteString(mins) : '' }));
  }, [editForm?.time_in, editForm?.time_out]);

  // ── Fetch week ──
  const fetchWeek = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyWeek(weekEnding);
      setWeek(res.week);
      setEntries(res.entries || []);
    } catch (err) {
      showToast(err.message || 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  }, [weekEnding, showToast]);

  useEffect(() => { fetchWeek(); }, [fetchWeek]);

  // ── Add entry ──
  async function handleAdd(e) {
    e.preventDefault();
    if (!form.entry_date || !form.job_number.trim() || !form.description.trim()) {
      showToast('Date, Job # and Description are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await addEntry({
        entry_date: form.entry_date,
        work_order: form.work_order.trim(),
        job_number: form.job_number.trim(),
        location: form.location,
        description: form.description.trim(),
        notes: form.notes.trim(),
        time_in: form.time_in,
        time_out: form.time_out,
        hours_worked: form.hours_worked,
      });
      showToast('Entry added ✓', 'success');
      // setForm(f => ({ ...f, work_order: '', job_number: '', description: '', notes: '' }));
      // ✅ Full reset — times go back to 07:00 / 15:30 every time
      setForm(initForm());
      fetchWeek();
    } catch (err) {
      showToast(err.message || 'Failed to add entry', 'error');
    } finally {
      setSaving(false);
    }
  }

  // ── Edit entry save ──
  async function handleEditSave() {
    
    if (!editForm.job_number.trim() || !editForm.description.trim()) {
      showToast('Job # and Description are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await updateEntry(editEntry.id, {
        entry_date: editForm.entry_date,
        work_order: editForm.work_order.trim(),
        job_number: editForm.job_number.trim(),
        location: editForm.location,
        description: editForm.description.trim(),
        notes: editForm.notes.trim(),
        time_in: editForm.time_in,
        time_out: editForm.time_out,
        hours_worked: editForm.hours_worked,
      });
      showToast('Entry updated ✓', 'success');
      setEditEntry(null); setEditForm(null);
      fetchWeek();
    } catch (err) {
      showToast(err.message || 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  }

  // ── Delete ──
  async function handleDelete(id) {
    if (!window.confirm('Remove this entry?')) return;
    try {
      await deleteEntry(id);
      showToast('Entry removed', 'warning');
      fetchWeek();
    } catch (err) {
      showToast(err.message || 'Delete failed', 'error');
    }
  }

  // ── Submit week ──
  async function handleSubmit() {
    if (!week?.id) return;
    setSubmitting(true);
    try {
      await submitWeek(week.id);
      showToast('Timesheet submitted for approval ✅', 'success');
      setSubmitOpen(false);
      fetchWeek();
    } catch (err) {
      showToast(err.message || 'Submit failed', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  // ── 7-day grid (Mon → Sun) ──
  const weekGrid = () => {
    const iso = toDisplay(week?.week_ending_date || weekEnding);
    const end = new Date(`${iso}T00:00:00`);
    const localToday = getToday();

    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(end);
      d.setDate(end.getDate() - (6 - i));
      const isoKey = formatFromDate(d);

      const rows = entries.filter(en => toDisplay(en.entry_date) === isoKey);
      const mins = rows.reduce((s, en) => s + toMinutes(en.hours_worked), 0);
      return {
        day: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][d.getDay()],
        date: isoKey,
        hrs: toHourMinuteString(mins), jobs: rows.length,
        hrsMins: mins,
        today: localToday === isoKey,
      };
    });
  };

  const totalMins = entries.reduce((s, e) => s + toMinutes(e.hours_worked), 0);
  const regMins = Math.min(totalMins, 40 * 60);
  const otMins = Math.max(totalMins - (40 * 60), 0);
  const totalHrs = toHourMinuteString(totalMins);
  const regHrs = toHourMinuteString(regMins);
  const otHrs = toHourMinuteString(otMins);
  const canEdit = !week || ['draft', 'rejected'].includes(week?.status);

  if (loading) return <Spinner text="Loading timesheet…" />;

  return (
    <div>
      {/* <PageHeader
        title="MY TIMESHEET"
        subtitle={`Week Ending: ${weekEnding}`}
        actions={canEdit && entries.length > 0
          ? <Btn onClick={() => setSubmitOpen(true)}>✅ Submit for Approval</Btn>
          : null}
      /> */}
      <PageHeader
        title="MY TIMESHEET"
        subtitle={
          <>
            Week Ending: <span style={{ color: 'red' }}>{weekEnding}</span>
          </>
        }
        actions={
          canEdit && entries.length > 0
            ? <Btn onClick={() => setSubmitOpen(true)}>✅ Submit for Approval</Btn>
            : null
        }
      />

      {/* ── Week selector — DatePicker ── */}
      {/* <div className="filter-row mb-4">
        <div className="filter-group">
          <span className="filter-label">Week Ending</span>
          <div style={{ width: '180px' }}>
            <DatePicker
              value={weekEnding}
              onChange={setWeekEnding}
            />
          </div>
        </div>
        {week && <StatusBadge status={week.status} />}
        {week?.status === 'rejected' && week.reviewer_notes && (
          <span className="alert alert-warn" style={{ padding: '0.25rem 0.625rem', fontSize: '0.75rem' }}>
            Rejected: {week.reviewer_notes}
          </span>
        )}
      </div> */}

      {/* ── 7-day grid ── */}
      <div className="week-grid mb-4">
        {weekGrid().map((d, i) => (
          <div key={i} className={`day-cell${d.today ? ' today' : ''}`}>
            {d.today && <span className="day-cell-today-tag">TODAY</span>}
            <div className="day-cell-day">{d.day}</div>
            <div className="day-cell-date">{d.date}</div>
            <div className={`day-cell-hrs${d.hrsMins > 0 ? ' has-hrs' : ' no-hrs'}`}>
              {d.hrsMins > 0 ? d.hrs : '—'}
            </div>
            <div className="day-cell-jobs">
              {d.jobs > 0 ? `${d.jobs} job${d.jobs > 1 ? 's' : ''}` : 'no entry'}
            </div>
          </div>
        ))}
      </div>

      {/* ── Add entry form ── */}
      {canEdit && (
        <div className="card mb-4">
          <CardTitle>➕ Add Daily Entry</CardTitle>
          <div className="card-body">
            <form onSubmit={handleAdd}>
              <div className="form-grid add-entry-grid mb-3">

                {/* ✅ Date — custom DatePicker */}
                <Field label="Date" required>
                  <DatePicker
                    value={form.entry_date}
                    onChange={val => setForm(f => ({ ...f, entry_date: val }))}
                  />
                </Field>

                <Field label="WO # (Work Order)">
                  <Input
                    type="text"
                    value={form.work_order}
                    onChange={upd('work_order')}
                    placeholder="e.g. WO-1021"
                    style={{ fontSize: '15px' }}
                  />
                </Field>

                <Field label="Unit / Job Number" required>
                  <Input
                    type="text"
                    value={form.job_number}
                    onChange={upd('job_number')}
                    placeholder="e.g. JOB-2841"
                    required
                    style={{ fontSize: '15px' }}
                  />
                </Field>
                <Field label="Loc" required>
                  <select
                    value={form.location}
                    onChange={upd('location')}
                    style={{
                      width: '100%',
                      padding: '0.48rem 0.75rem',
                      borderRadius: '6px',
                      border: '1.5px solid #d1d5db',
                      fontSize: '0.875rem',
                      color: '#111827',
                      background: '#fff',
                      outline: 'none',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <option value="Shop">Shop</option>
                    <option value="Off site">Off site</option>
                  </select>
                </Field>

                <Field label="Time In" required>
                  <Input
                    type="time"
                    value={form.time_in}
                    onChange={upd('time_in')}
                    required
                    style={{ fontSize: '15px' }}
                  />
                </Field>

                <Field label="Time Out" required>
                  <Input
                    type="time"
                    value={form.time_out}
                    onChange={upd('time_out')}
                    required
                    style={{ fontSize: '15px' }}
                  />
                </Field>

                <Field label="Hours Worked">
                  <Input
                    type="number"
                    value={form.hours_worked}
                    readOnly
                    placeholder="Auto"
                    step="0.5"
                    style={{ fontSize: '15px' }}
                  />
                </Field>

                <div className="add-entry-span-full">
                  <Field label="Work Description" required>
                    <textarea
                      value={form.description}
                      onChange={upd('description')}
                      placeholder="Work description…"
                      required
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        fontSize: '15px',
                        lineHeight: '1.5',
                      }}
                    />
                  </Field>
                </div>

                <div className="add-entry-span-full">
                  <Field label="Notes">
                    <textarea
                      value={form.notes}
                      onChange={upd('notes')}
                      placeholder="Notes…"
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        fontSize: '15px',
                        lineHeight: '1.5',
                      }}
                    />
                  </Field>
                </div>

                <div className="add-entry-span-full">
                  <Field label=" ">
                    <Btn
                      type="submit"
                      disabled={saving}
                      style={{ width: '100%', fontSize: '15px', padding: '9px 12px' }}
                    >
                      {saving ? 'SAVING…' : 'ADD ENTRY'}
                    </Btn>
                  </Field>
                </div>

              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Entries table ── */}
      <div className="card">
        <div className="card-header">
          <span>This Week's Entries</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
            Total: <strong style={{ color: '#cc1111' }}>{totalHrs}</strong> hrs
          </span>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                {['Date', 'Day', 'WO #', 'Unit / Job #', 'Loc', 'Description', 'Time In', 'Time Out', 'Hours', 'Status', 'Action'].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan={10} className="tbl-empty">No entries yet. Add your first entry above.</td></tr>
              ) : entries.map(en => (
                <tr key={en.id}>
                  <td className="td-mono">{toDisplay(en.entry_date)}</td>
                  <td className="td-muted" style={{ fontWeight: 700, fontSize: '0.65rem' }}>
                    {getDayLabel(en.entry_date)}
                  </td>
                  <td className="td-mono" style={{ color: '#7c3aed' }}>{en.work_order || '—'}</td>
                  <td className="td-mono td-red">{en.job_number}</td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      background: en.location === 'Off site' ? '#fef3c7' : '#dcfce7',
                      color: en.location === 'Off site' ? '#b45309' : '#15803d',
                    }}>
                      {en.location || 'Shop'}
                    </span>
                  </td>
                  <td>
                    <TruncatedText text={en.description} maxLen={truncLen} style={{ fontWeight: 500 }} />
                    {en.notes && (
                      <TruncatedText
                        text={`🔧 ${en.notes}`}
                        maxLen={truncLen}
                        style={{ fontSize: '0.72rem', color: '#6b7280', fontStyle: 'italic', marginTop: '0.15rem' }}
                      />
                    )}
                  </td>
                  {/* <td><span className="time-chip in">{formatTime(en.time_in)}</span></td> */}
                  {/* <td><span className="time-chip out">{formatTime(en.time_out)}</span></td> */}
                  {/* <td><span className="time-chip in">{en.time_in?.slice(0, 5) || '—'}</span></td> */}
                  {/* <td><span className="time-chip out">{en.time_out?.slice(0, 5) || '—'}</span></td> */}
                  <td><span className="time-chip in">{to12Hour(en.time_in)}</span></td>
                  <td><span className="time-chip out">{to12Hour(en.time_out)}</span></td>
                  <td>
                    <span className={`hrs-val${toMinutes(en.hours_worked) > (8 * 60) ? ' over8' : ''}`}>
                      {toHourMinuteString(toMinutes(en.hours_worked))}
                      <span style={{ fontSize: '0.6rem', opacity: 0.5, marginLeft: '0.125rem' }}>h</span>
                    </span>
                  </td>
                  <td><StatusBadge status={week?.status || 'draft'} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'nowrap' }}>
                      <button className="btn btn-xs"
                        style={{ background: '#1f2937', color: '#fff', border: 'none', whiteSpace: 'nowrap' }}
                        onClick={() => setViewEntry(en)}>
                        👁 VIEW
                      </button>
                      {canEdit && (
                        <button className="btn btn-xs"
                          style={{ background: '#f59e0b', color: '#fff', border: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => {
                            setEditEntry(en);
                            setEditForm({
                              entry_date: toDisplay(en.entry_date),
                              work_order: en.work_order || '',
                              job_number: en.job_number || '',
                              location: en.location || 'Shop',
                              description: en.description || '',
                              notes: en.notes || '',
                              time_in: en.time_in?.slice(0, 5) || '07:00',
                              time_out: en.time_out?.slice(0, 5) || '15:30',
                              hours_worked: en.hours_worked || '',
                            });
                          }}>
                          ✏️ EDIT
                        </button>
                      )}
                      {canEdit && (
                        <button className="btn btn-danger btn-xs" onClick={() => handleDelete(en.id)}>
                          DEL
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Submit modal ── */}
      <Modal open={submitOpen} title="Submit Timesheet for Approval"
        onClose={() => setSubmitOpen(false)}
        footer={<>
          <Btn variant="secondary" onClick={() => setSubmitOpen(false)}>CANCEL</Btn>
          <Btn variant="success" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'SUBMITTING…' : '✅ CONFIRM SUBMIT'}
          </Btn>
        </>}>
        <p className="text-sm" style={{ color: '#6b7280', marginBottom: '1rem' }}>
          Submit week ending <strong style={{ color: '#cc1111' }}>{weekEnding}</strong> for supervisor approval.
          You cannot edit entries after submission.
        </p>
        <div className="info-block">
          {[
            ['Total Hours', `${totalHrs} hrs`, totalMins > 0],
            ['Regular (≤40)', `${regHrs} hrs`, false],
            ['Overtime (>40)', `${otHrs} hrs`, otMins > 0],
          ].map(([l, v, red]) => (
            <div key={l} className="info-row">
              <span>{l}</span>
              <span style={red ? { color: '#cc1111' } : {}}>{v}</span>
            </div>
          ))}
        </div>
      </Modal>

      {/* ── View Entry modal ── */}
      <Modal open={!!viewEntry} title="Entry Details"
        onClose={() => setViewEntry(null)}
        footer={<Btn variant="secondary" onClick={() => setViewEntry(null)}>CLOSE</Btn>}>
        {viewEntry && (
          <div className="info-block">
            {[
              ['Date', toDisplay(viewEntry.entry_date)],
              ['Day', getDayLabel(viewEntry.entry_date)],
              ['WO #', viewEntry.work_order || '—'],
              ['Unit / Job #', viewEntry.job_number],
              ['Loc', viewEntry.location || 'Shop'],
              // ['Time In', formatTime(viewEntry.time_in)],
              // ['Time Out', formatTime(viewEntry.time_out)],
              ['Time In', to12Hour(viewEntry.time_in)],
              ['Time Out', to12Hour(viewEntry.time_out)],
              ['Hours', `${toHourMinuteString(toMinutes(viewEntry.hours_worked))} hrs`],
            ].map(([l, v]) => (
              <div key={l} className="info-row">
                <span>{l}</span>
                <span>{v}</span>
              </div>
            ))}
            {[['Description', viewEntry.description], ['Notes', viewEntry.notes || '—']].map(([l, v]) => (
              <div key={l} className="info-row-wrap">
                <span>{l}</span>
                <span>{v}</span>
              </div>
            ))}
            <div className="info-row">
              <span>Status</span>
              <span>{week?.status || 'draft'}</span>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Edit Entry modal ── */}
      <Modal open={!!editEntry} title="Edit Entry"
        onClose={() => { setEditEntry(null); setEditForm(null); }}
        footer={<>
          <Btn variant="secondary" onClick={() => { setEditEntry(null); setEditForm(null); }}>CANCEL</Btn>
          <Btn variant="success" onClick={handleEditSave} disabled={saving}>
            {saving ? 'SAVING…' : '💾 SAVE CHANGES'}
          </Btn>
        </>}>
        {editForm && (
          <div className="form-grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(9rem, 1fr))', gap: '0.75rem' }}>

            {/* ✅ Date — custom DatePicker in edit modal */}
            <Field label="Date" required>
              <DatePicker
                value={editForm.entry_date}
                onChange={val => setEditForm(f => ({ ...f, entry_date: val }))}
              />
            </Field>

            <Field label="WO # (Work Order)">
              <Input type="text" value={editForm.work_order} onChange={updEdit('work_order')} placeholder="e.g. WO-1021" />
            </Field>
            <Field label="Unit / Job Number" required>
              <Input type="text" value={editForm.job_number} onChange={updEdit('job_number')} placeholder="e.g. JOB-2841" required />
            </Field>
            <Field label="Loc" required>
              <select
                value={editForm.location}
                onChange={updEdit('location')}
                style={{
                  width: '100%',
                  padding: '0.48rem 0.75rem',
                  borderRadius: '6px',
                  border: '1.5px solid #d1d5db',
                  fontSize: '0.875rem',
                  color: '#111827',
                  background: '#fff',
                  outline: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <option value="Shop">Shop</option>
                <option value="Off site">Off site</option>
              </select>
            </Field>
            <Field label="Time In">
              <Input type="time" value={editForm.time_in} onChange={updEdit('time_in')} />
            </Field>
            <Field label="Time Out">
              <Input type="time" value={editForm.time_out} onChange={updEdit('time_out')} />
            </Field>
            <Field label={`Hours Worked${editForm.hours_worked ? ` (${editForm.hours_worked})` : ''}`}>
              <Input type="number" value={editForm.hours_worked} readOnly placeholder="Auto" step="0.5" />
            </Field>
            <div className="add-entry-span-full">
              <Field label="Work Description" required>
                <textarea
                  value={editForm.description}
                  onChange={updEdit('description')}
                  placeholder="Work description…"
                  required
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    fontSize: '15px',
                    lineHeight: '1.5',
                    boxSizing: 'border-box',
                  }}
                />
              </Field>
            </div>
            <div className="add-entry-span-full">
              <Field label="Notes">
                <textarea
                  value={editForm.notes}
                  onChange={updEdit('notes')}
                  placeholder="Additional notes…"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    fontSize: '15px',
                    lineHeight: '1.5',
                    boxSizing: 'border-box',
                  }}
                />
              </Field>
            </div>
          </div>
        )}
      </Modal>

      <Toast message={toast.message} type={toast.type} onClose={clearToast} />
    </div>
  );
}